import { FunctionComponent, FormEvent, useEffect, useState } from 'react';
import { Box, Select, TextField, PARTNER_ACCOUNT_STATUSES, PartnerAccountStatus } from 'vopay';
import { Button } from 'UI';
import { useTranslation } from 'react-i18next';
import styles from './PartnerList.module.scss';

export type PartnerListSearchValues = {
  status: PartnerAccountStatus | '';
  searchText: string;
};

type PartnerListSearchFormProps = {
  status: PartnerAccountStatus | '';
  searchText: string;
  onSearch: (values: PartnerListSearchValues) => void;
};

const PartnerListSearchForm: FunctionComponent<PartnerListSearchFormProps> = ({ status, searchText, onSearch }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.List.SearchForm' });
  const { t: tStatus } = useTranslation('translations', { keyPrefix: 'Global.PartnerAccountStatus' });

  const [statusValue, setStatusValue] = useState<PartnerAccountStatus | ''>(status);
  const [searchTextValue, setSearchTextValue] = useState(searchText);

  // Keep inputs in sync when the applied filters change externally (e.g. back/forward navigation).
  useEffect(() => {
    setStatusValue(status);
    setSearchTextValue(searchText);
  }, [status, searchText]);

  const statusOptions = PARTNER_ACCOUNT_STATUSES.map((partnerStatus) => ({
    label: tStatus(partnerStatus),
    value: partnerStatus,
  }));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSearch({ status: statusValue, searchText: searchTextValue });
  };

  return (
    <Box ai="center" pb="md" base="8">
      <form onSubmit={handleSubmit} className={styles.searchArea}>
        <div className={styles.statusField}>
          <Select
            version="2"
            showClear
            placeholder={t('statusPlaceholder', { defaultValue: 'Status' })}
            options={statusOptions}
            name="status"
            onChange={(e) => setStatusValue(e.target.value as PartnerAccountStatus | '')}
            value={statusValue || ''}
            sort={false}
          />
        </div>

        <div className={styles.searchField}>
          <TextField
            name="searchText"
            placeholder={t('keywordPlaceholder', { defaultValue: 'Search by name or account ID' })}
            onChange={(e) => setSearchTextValue(e.target.value)}
            value={searchTextValue}
            variant="outlineShadow"
            containerWidth="100%"
          />
        </div>

        <Button id="partners-search" type="submit" colorScheme="primary" size="small">
          {t('searchButton', { defaultValue: 'Search' })}
        </Button>
      </form>
    </Box>
  );
};

export default PartnerListSearchForm;
